// ROM handling for the dev IDE: import a file, hash it, cache the bytes, and
// restore the last-used ROM on reload. Thin wrapper over the shared rom-cache
// (IndexedDB bytes + a localStorage recency index) so the IDE never re-prompts
// for a ROM it already has.

import { getRom, putRom, rememberRom, readRecentRoms } from "../src/rom-cache.js";
import type { LoadedRom } from "./runner.js";

/** SHA-1 hex of the ROM bytes — the same identity the player uses. */
export async function hashRom(bytes: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-1", bytes as unknown as BufferSource);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Read a picked file, hash + cache it, and return it ready to run. */
export async function importRomFile(file: File): Promise<LoadedRom> {
  const data = new Uint8Array(await file.arrayBuffer());
  const hash = await hashRom(data);
  await putRom(hash, data);
  rememberRom({ hash, name: file.name });
  return { data, hash, name: file.name };
}

/** Restore a previously-cached ROM by reference, or null if it's gone. */
export async function restoreRom(ref: { hash: string; name: string } | null): Promise<LoadedRom | null> {
  if (!ref) return null;
  const data = await getRom(ref.hash);
  if (!data) return null;
  return { data, hash: ref.hash, name: ref.name };
}

/** The most-recently-used cached ROM reference, if any. */
export function lastRomRef(): { hash: string; name: string } | null {
  return readRecentRoms()[0] ?? null;
}
